import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';

import { translateObject } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';

import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { DialogService } from 'app/services/dialog.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { Comment, CommentTarget } from '../../../../../providers/bridge-api/dashboard.service';
import { CommentDataChange } from '../../../services/chart-report/chart-report-abstract/chart-report-abstract';
import { WidgetCommentsHandlerService } from '../../../services/widget-comments-handler.service';

export interface WidgetCommentListOptions {
  isDashboardLocked: boolean;
  smartSummaryIds: string[];
}

@EnableClassTelemetry()
@Component({
  selector: 'app-widget-comment-list',
  templateUrl: './widget-comment-list.component.html',
  styleUrls: ['./widget-comment-list.component.scss'],
  standalone: false,
})
export class WidgetCommentListComponent implements OnInit, AfterViewInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @ViewChildren('commentCard', { read: ElementRef }) commentCards: QueryList<ElementRef>;
  @ViewChild('listWrapper') listWrapper: ElementRef<HTMLDivElement>;

  @Input() options: WidgetCommentListOptions;

  @Output() smartSummaryOpen = new EventEmitter<string>();
  @Output() deleteComment = new EventEmitter<Comment>();
  @Output() openComment = new EventEmitter<Comment>();
  @Output() showDetails = new EventEmitter<Comment>();
  @Output() setAnnotationSelected = new EventEmitter<CommentTarget>();

  private commentsHandlerService = inject(WidgetCommentsHandlerService);
  private dialogService = inject(DialogService);

  comments: Comment[] = [];
  visibleComments: Comment[] = [];
  commentChanges: Map<string, CommentDataChange>;
  selectedComment: Comment;
  commentTarget: CommentTarget;
  smartSummaryExistenceMap = new Map<string, boolean>();
  commentVisibilitySet = new Set<string>();

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.commentsHandlerService.comments$.pipe(takeUntil(this.destroy$)).subscribe((comments) => {
      this.comments = comments;
      this.fillSmartSummaryCache();
    });

    this.commentsHandlerService.visibleComments$
      .pipe(takeUntil(this.destroy$))
      .subscribe((visibleComments) => {
        this.visibleComments = visibleComments;
        this.commentVisibilitySet = new Set(this.visibleComments.map((c) => c.id));
      });

    this.commentsHandlerService.commentTarget$
      .pipe(takeUntil(this.destroy$))
      .subscribe((commentTarget) => {
        this.commentTarget = commentTarget;
        this.scrollSelectedCommentToView();
      });

    this.commentsHandlerService.commentChanges$
      .pipe(takeUntil(this.destroy$))
      .subscribe((commentChanges) => {
        this.commentChanges = commentChanges;
      });
  }

  ngAfterViewInit(): void {
    this.scrollSelectedCommentToView();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private fillSmartSummaryCache() {
    this.smartSummaryExistenceMap.clear();
    this.comments
      .filter((c) => c.smartSummaryId)
      .forEach((c) =>
        this.smartSummaryExistenceMap.set(
          c.smartSummaryId,
          this.options.smartSummaryIds.includes(c.smartSummaryId)
        )
      );
  }

  private scrollSelectedCommentToView(): void {
    this.selectedComment = null;
    // We need a setTimeout because the DOM is not stable enough to calculate scroll position to scrollTo
    setTimeout(() => {
      const idx = this.comments.findIndex((c) => c.target.pointId === this.commentTarget?.pointId);
      if (idx > -1) {
        this.selectedComment = this.comments[idx];
        this.commentCards
          .get(idx)
          .nativeElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        this.setAnnotationSelected.emit(this.selectedComment.target);
      }
    }, 200);
  }

  onOpenSmartSummary(summaryId: string) {
    this.smartSummaryOpen.emit(summaryId);
  }

  onDelete(comment: Comment): void {
    const translations = translateObject('widgets.comments.delete-dialog');
    this.dialogService
      .showSimpleDialog({
        title: translations['title'],
        content: translations['content'],
        confirmTitle: translations['confirm'],
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.deleteComment.emit(comment);
        }
      });
  }

  onOpen(comment: Comment): void {
    this.openComment.emit(comment);
  }
}
